import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import PrintDialog from './eductionalPrintDialog';
import Footer from './Footer';
const AppUrl = {otherBillsInsuranceGetEngineCapacity:"users/other-bills/insurance/get-engine-capacity",
                otherBillsInsuranceGetEngineCapacity:"users/other-bills/insurance/get-engine-capacity",
                otherBillsInsuranceGetStateCode:"users/other-bills/insurance/get-state-code",
                otherBillsInsuranceBrand:"users/other-bills/insurance/brand",
                otherBillsInsuranceModel:"users/other-bills/insurance/model",
                otherBillsInsurancePay: "users/other-bills/insurance/pay" ,
                otherBillsGetVariation:'users/other-bills/getvariation',
                otherBillsInsuranceGetColor:'users/other-bills/insurance/get-color',
                otherBillsInsuranceGetLgaCode:'users/other-bills/insurance/get-lga-code'}

const InsuranceServiceScreen = () => {
    const { http, user } = AuthenticateService();

  // Form state
  const [formData, setFormData] = useState({
    phone: '',
    pin: '',
    plateNumber: '',
    chassisNumber: '',
    engineNumber: '',
    insuredName: '',
    yearOfMake: '',
    email: ''
  });

  // Dropdown options
  const [colors, setColors] = useState([]);
  const [engineCapacities, setEngineCapacities] = useState([]);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [insuranceTypes, setInsuranceTypes] = useState([]);

  // Selected values
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedEngineCapacity, setSelectedEngineCapacity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedLga, setSelectedLga] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedInsuranceType, setSelectedInsuranceType] = useState('');

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);
  const [loadingCapacities, setLoadingCapacities] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingInsuranceTypes, setLoadingInsuranceTypes] = useState(false);

  // Success dialog
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    certUrl: '',
    balance: '',
    transactionData: null
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Fetch all initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([
        fetchColors(),
        fetchEngineCapacities(),
        fetchStates(),
        fetchBrands(),
        fetchInsuranceTypes()
      ]);
    };
    fetchInitialData();
  }, []);

  // Helper to get icon for insurance type
  const getInsuranceTypeIcon = (typeName) => {
    const lowerName = typeName.toLowerCase();
    if (lowerName.includes('private')) return 'directions_car';
    if (lowerName.includes('commercial')) return 'local_shipping';
    if (lowerName.includes('tricycle')) return 'moped';
    if (lowerName.includes('motorcycle')) return 'two_wheeler';
    return 'car_repair';
  };

  // API calls
  const fetchInsuranceTypes = async () => {
    setLoadingInsuranceTypes(true);
    try {
      const response = await http.get(`${AppUrl.otherBillsGetVariation}?serviceID=ui-insure`);
      if (response.data.status) {
        setInsuranceTypes(response.data.data || []);
      }
    } finally {
      setLoadingInsuranceTypes(false);
    }
  };

  const fetchColors = async () => {
    setLoadingColors(true);
    try {
      const response = await http.get(AppUrl.otherBillsInsuranceGetColor);
      if (response.data.status) {
        setColors(response.data.data || []);
      }
    } finally {
      setLoadingColors(false);
    }
  };

  const fetchEngineCapacities = async () => {
    setLoadingCapacities(true);
    try {
      const response = await http.get(AppUrl.otherBillsInsuranceGetEngineCapacity);
      if (response.data.status) {
        setEngineCapacities(response.data.data || []);
      }
    } finally {
      setLoadingCapacities(false);
    }
  };

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const response = await http.get(AppUrl.otherBillsInsuranceGetStateCode);
      if (response.data.status) {
        setStates(response.data.data || []);
      }
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchLgas = async (stateCode) => {
    setLoadingLgas(true);
    try {
      const response = await http.get(`${AppUrl.otherBillsInsuranceGetLgaCode}/${stateCode}`);
      if (response.data.status) {
        setLgas(response.data.data?.original || []);
      }
    } finally {
      setLoadingLgas(false);
    }
  };

  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const response = await http.get(AppUrl.otherBillsInsuranceBrand);
      if (response.data.status) {
        setBrands(response.data.data || []);
      }
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchModels = async (brandCode) => {
    setLoadingModels(true);
    try {
      const response = await http.get(`${AppUrl.otherBillsInsuranceModel}/${brandCode}`);
      if (response.data.status) {
        setModels(response.data.data?.original || []);
      }
    } finally {
      setLoadingModels(false);
    }
  };

  // Form submission
  const submitInsurance = async () => {
    if (!selectedInsuranceType || !selectedColor || !selectedEngineCapacity ||
        !selectedState || !selectedLga || !selectedBrand || !selectedModel ||
        !formData.plateNumber || !formData.chassisNumber || !formData.engineNumber ||
        !formData.phone || !formData.pin) {
      setSnackbar({ open: true, message: 'Please fill all required fields' });
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        serviceID: 'ui-insure',
        variation_code: selectedInsuranceType,
        phone: formData.phone,
        Insured_Name: formData.insuredName,
        engine_capacity: selectedEngineCapacity,
        Chasis_Number: formData.chassisNumber,
        Plate_Number: formData.plateNumber,
        billersCode: formData.plateNumber,
        vehicle_make: selectedBrand,
        vehicle_color: selectedColor,
        vehicle_model: selectedModel,
        YearofMake: formData.yearOfMake,
        state: selectedState,
        lga: selectedLga,
        email: formData.email,
        transaction_pin: formData.pin,
      };

      const response = await http.post(
        AppUrl.otherBillsInsurancePay,
        payload
      );

      if (response.data.status) {

        handleSuccessResponse(response.data);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message?.message || 'Insurance purchase failed'
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.toString()}` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessResponse = (response) => {
    const transactionData = extractNestedData(response, ['data', 'data']);
    const certUrl = transactionData?.certUrl;
    const balance = extractNestedData(response, ['data', 'balance']);
    console.log('transactionData?.certUrl',transactionData)

    setSuccessDialog({
      open: true,
      certUrl: certUrl || '',
      balance: balance?.toString() || '',
      transactionData: transactionData || null
    });
  };

  const extractNestedData = (data, keys) => {
    let result = data;
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        return null;
      }
    }
    return result;
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedLga('');
    setLgas([]);
    if (value) fetchLgas(value);
  };

  const handleBrandChange = (value) => {
    setSelectedBrand(value);
    setSelectedModel('');
    setModels([]);
    if (value) fetchModels(value);
  };

  // Render components
  const InsuranceTypeSelector = () => {
    if (loadingInsuranceTypes) {
      return <CircularProgress />;
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Insurance Type</Typography>
        <Box sx={{ display: 'flex', overflowX: 'auto', gap: 2, py: 1 }}>
          {insuranceTypes.map((type) => (
            <Card
              key={type.variation_code}
              sx={{
                minWidth: 120,
                cursor: 'pointer',
                backgroundColor: selectedInsuranceType === type.variation_code ?
                  'primary.light' : 'background.paper'
              }}
              onClick={() => setSelectedInsuranceType(type.variation_code)}
            >
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* <Icon sx={{ fontSize: 30 }}>{getInsuranceTypeIcon(type.name)}</Icon> */}
                <div className="text-warning"><span className="fa fa-shield"></span></div>
                <Typography fontWeight="bold">{type.name}</Typography>
                <Typography>₦{type.variation_amount}</Typography>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    );
  };

  const DropdownField = ({
    label,
    value,
    options,
    loading,
    onChange,
    getOptionLabel = (opt) => opt.name,
    getOptionValue = (opt) => opt.id
  }) => (
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
      >
        {loading ? (
          <MenuItem disabled><CircularProgress size={24} /></MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem key={getOptionValue(option)} value={getOptionValue(option)}>
              {getOptionLabel(option)}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );

  return (
    <>

      <Sidebar />
      <Header user={user} />
      <div className="content bg-light" >
        <AppBar position="static">
            <Typography variant="h6" component="div" sx={{ p: 2 }}>
            Motor Insurance
            </Typography>
        </AppBar>

            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>Vehicle Details</Typography>

                <InsuranceTypeSelector />

                <DropdownField
                label="Vehicle Color"
                value={selectedColor}
                options={colors}
                loading={loadingColors}
                onChange={setSelectedColor}
                getOptionLabel={(opt) => opt.ColourName}
                getOptionValue={(opt) => opt.ColourCode}
                />

                <DropdownField
                label="Engine Capacity"
                value={selectedEngineCapacity}
                options={engineCapacities}
                loading={loadingCapacities}
                onChange={setSelectedEngineCapacity}
                getOptionLabel={(opt) => opt.CapacityName}
                getOptionValue={(opt) => opt.CapacityCode}
                />

                <DropdownField
                label="State"
                value={selectedState}
                options={states}
                loading={loadingStates}
                onChange={handleStateChange}
                getOptionLabel={(opt) => opt.StateName}
                getOptionValue={(opt) => opt.StateCode}
                />

                <DropdownField
                label="LGA"
                value={selectedLga}
                options={lgas}
                loading={loadingLgas}
                onChange={setSelectedLga}
                getOptionLabel={(opt) => opt.LGAName}
                getOptionValue={(opt) => opt.LGACode}
                />

                <DropdownField
                label="Vehicle Brand"
                value={selectedBrand}
                options={brands}
                loading={loadingBrands}
                onChange={handleBrandChange}
                getOptionLabel={(opt) => opt.VehicleMakeName}
                getOptionValue={(opt) => opt.VehicleMakeCode}
                />

                <DropdownField
                label="Vehicle Model"
                value={selectedModel}
                options={models}
                loading={loadingModels}
                onChange={setSelectedModel}
                getOptionLabel={(opt) => opt.VehicleModelName}
                getOptionValue={(opt) => opt.VehicleModelCode}
                />

                <TextField
                label="Plate Number"
                name="plateNumber"
                value={formData.plateNumber}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                />

                <TextField
                label="Chassis Number"
                name="chassisNumber"
                value={formData.chassisNumber}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                />

                <TextField
                label="Engine Number"
                name="engineNumber"
                value={formData.engineNumber}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                />

                <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>User Details</Typography>

                <TextField
                label="Insured Name"
                name="insuredName"
                value={formData.insuredName}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                />

                <TextField
                label="Year of Make"
                name="yearOfMake"
                value={formData.yearOfMake}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                type="number"
                />

                <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                type="email"
                />

                <TextField
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 2 }}
                type="tel"
                />

                <TextField
                label="Transaction PIN"
                name="pin"
                value={formData.pin}
                onChange={handleInputChange}
                fullWidth
                sx={{ mb: 3 }}
                type="password"
                />

                <Button
                variant="contained"
                size="large"
                onClick={submitInsurance}
                disabled={isLoading}
                sx={{ py: 2 }}
                >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Purchase Insurance'}
                </Button>
            </Box>

            {/* Success Dialog */}
            <Dialog open={successDialog.open} maxWidth="sm" fullWidth>
                <DialogTitle>Insurance Successful</DialogTitle>
                <DialogContent>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <img src="/logo.png" width="200"/>
                    {/* /<Icon color="success" sx={{ fontSize: 60 }}>check_circle</Icon> */}
                    <Typography variant="h5" gutterBottom>Insurance Purchase Successful!</Typography>
                </Box>
                {successDialog?.transactionData?.content?.transactions?.product_name && (
                    <Typography sx={{ mb: 2 }}>
                    Product Name: {(successDialog?.transactionData?.content?.transactions?.product_name)}
                    </Typography>
                )}

                {successDialog.balance && (
                    <Typography sx={{ mb: 2 }}>
                    New Balance: ₦{parseFloat(successDialog.balance).toFixed(2)}
                    </Typography>
                )}
                {successDialog?.transactionData?.amount && (
                    <Typography sx={{ mb: 2 }}>
                    Amount: ₦{(successDialog?.transactionData?.amount)}
                    </Typography>
                )}

                {successDialog.transactionData?.purchased_code && (
                    <Box sx={{ mb: 2 }}>
                    <Typography fontWeight="bold">Insurance Details:</Typography>
                    <Typography>{successDialog.transactionData.purchased_code}</Typography>
                    </Box>
                )}

                {successDialog.certUrl && (
                    <Button
                    variant="contained"
                    fullWidth
                    sx={{ mb: 2 }}
                    onClick={() => window.open(successDialog.certUrl, '_blank')}
                    >
                    Download Insurance Certificate
                    </Button>
                )}
                </DialogContent>
                <DialogActions>
                <Button
                    onClick={() => {
                    setSuccessDialog({ ...successDialog, open: false });
                    // navigate('/home');
                    }}
                >
                    Return Home
                </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
      </div>
      <Footer />
    </>
  );
};

export default InsuranceServiceScreen;